"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.asyncEither = exports.asyncRight = exports.asyncLeft = exports.AsyncEither = exports.isPromise = void 0;
const Either_1 = require("./Either");
const errorHandler = (errorValue) => (err) => Either_1.Either.left(typeof errorValue === 'function' ? errorValue(err) : errorValue);
const isPromise = (v) => !!v && typeof v === 'object' && v !== null && 'then' in v;
exports.isPromise = isPromise;
const errorWrap = async (f, errorValue, ...args) => {
    try {
        let value = f(...args);
        if (typeof value === 'object' && 'then' in value) {
            value = await value;
        }
        return Either_1.Either.right(value);
    }
    catch (e) {
        return Either_1.Either.left(typeof errorValue === 'function' ? errorValue(e) : errorValue);
    }
};
class AsyncEither {
    constructor(value, errorValue) {
        this.value = value;
        this.errorValue = errorValue;
        this.isLeft = () => this.value.then(Either_1.Either.isLeft, () => true);
        this.isRight = () => this.value.then(Either_1.Either.isRight, () => false);
        this.leftMap = (f, errorValue) => new AsyncEither(this.value.then(Either_1.Either.cata(value => errorWrap(f, errorValue, value).then(v => Either_1.Either.left(v.value)))(value => Promise.resolve(Either_1.Either.right(value))), errorHandler(errorValue)), errorValue);
        this.leftFlatMap = (f) => new AsyncEither(this.value
            .then(value => Either_1.Either.cata(val => {
            const newEith = f(val);
            if (newEith instanceof AsyncEither) {
                return newEith;
            }
            else {
                return new AsyncEither(Promise.resolve(newEith), this.errorValue);
            }
        })(right => exports.asyncRight(right, this.errorValue))(value).value)
            .catch(errorHandler(this.errorValue)), this.errorValue);
        this.map = (f, errorValue = this.errorValue) => new AsyncEither(this.value
            .then(Either_1.Either.cata(value => Promise.resolve(Either_1.Either.left(value)))(value => errorWrap(f, errorValue, value)))
            .catch(errorHandler(errorValue)), errorValue);
        this.flatMap = (f, errorValue = this.errorValue) => new AsyncEither(this.value
            .then(value => Either_1.Either.cata(exports.asyncLeft)(val => {
            const newEith = f(val);
            if (newEith instanceof AsyncEither) {
                return newEith;
            }
            else {
                return new AsyncEither(Promise.resolve(newEith), errorValue);
            }
        })(value).value)
            .catch(errorHandler(errorValue)), errorValue);
        this.join = () => this.value;
        this.fullJoin = () => this.value.then(Either_1.Either.cata(v => Promise.reject(v))(v => Promise.resolve(v)));
        this.cata = (lf, rf) => this.value.then(Either_1.Either.cata(lf)(rf));
        this.tap = (rf, errorValue = this.errorValue) => new AsyncEither(this.value
            .then(Either_1.Either.cata(left => Promise.resolve(Either_1.Either.left(left)))(right => errorWrap(rf, errorValue, right).then(() => Either_1.Either.right(right))))
            .catch(errorHandler(errorValue)), errorValue);
        this.leftTap = (lf, errorValue = this.errorValue) => new AsyncEither(this.value
            .then(Either_1.Either.cata(left => errorWrap(lf, errorValue, left).then(() => Either_1.Either.left(left)))(right => Promise.resolve(Either_1.Either.right(right))))
            .catch(errorHandler(errorValue)), errorValue);
        this.setErrorValue = (errorValue) => new AsyncEither(this.value, errorValue);
        this.apply = (value, errorValue = this.errorValue) => this.map(func => func(value), errorValue);
        this.flatApply = (value, errorValue = this.errorValue) => this.flatMap(func => func(value), errorValue);
    }
    static All(values) {
        const results = Promise.all(values.map(value => value.fullJoin()));
        return exports.asyncRight(results, (err) => err);
    }
    filter(predicate, failedFilterResult, errorValue = this.errorValue) {
        return new AsyncEither(this.value
            .then(Either_1.Either.cata(l => Promise.resolve(Either_1.Either.left(l)))(async (r) => {
            try {
                const pred = predicate(r);
                const keep = await (pred instanceof AsyncEither
                    ? pred.value
                    : Promise.resolve(pred).then(Either_1.Either.right));
                return Either_1.Either.flatMap(shouldKeep => shouldKeep ? Either_1.Either.right(r) : Either_1.Either.left(failedFilterResult))(keep);
            }
            catch (e) {
                return errorHandler(errorValue)(e);
            }
        }))
            .catch(errorHandler(errorValue)), errorValue);
    }
    async then(onfulfilled, onRejectedWithLongName) {
        try {
            const val = await this.value;
            return onfulfilled
                ? onfulfilled(val)
                : Promise.resolve(val);
        }
        catch (val1) {
            if (onRejectedWithLongName) {
                return onRejectedWithLongName(Either_1.Either.left(val1));
            }
            return Either_1.Either.left(val1);
        }
    }
}
exports.AsyncEither = AsyncEither;
AsyncEither.Left = (value) => new AsyncEither(Promise.resolve(Either_1.Either.left(value)), value);
AsyncEither.Right = (value, errorValue) => new AsyncEither(exports.isPromise(value)
    ? value.then(v => Either_1.Either.right(v), errorHandler(errorValue))
    : Promise.resolve(Either_1.Either.right(value)), errorValue);
exports.asyncLeft = AsyncEither.Left;
exports.asyncRight = AsyncEither.Right;
const asyncEither = (eith, errorValue) => Either_1.Either.cata(exports.asyncLeft)(r => exports.asyncRight(r, errorValue))(eith);
exports.asyncEither = asyncEither;
//# sourceMappingURL=AsyncEither.js.map